import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ConfirmationModal = ({ helper, slot, onClose, onConfirm, isSubmitting = false, onViewDashboard }) => {
  const [bookingDetails, setBookingDetails] = useState({
    topic: '',
    notes: '',
    location: helper?.location || ''
  });

  const handleConfirm = () => {
    onConfirm(bookingDetails);
  };

  const getCommunicationIcon = (method) => {
    switch (method) {
      case 'video': return 'Video';
      case 'audio': return 'Phone';
      case 'phone': return 'PhoneCall';
      default: return 'Calendar';
    }
  };

  const getCommunicationLabel = (method) => {
    switch (method) {
      case 'video': return 'Video Call';
      case 'audio': return 'Audio Call';
      case 'phone': return 'Phone Call';
      default: return 'Session';
    }
  };

  return (
    <div className="fixed inset-0 z-[900] flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm">
      <div className="bg-card rounded-lg border border-border shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-center mb-6">
            <div className="w-16 h-16 rounded-full bg-success/10 flex items-center justify-center">
              <Icon name="CheckCircle2" size={32} className="text-success" />
            </div>
          </div>

          <h2 className="text-2xl font-semibold text-foreground text-center mb-2">
            Booking Confirmed!
          </h2>
          <p className="text-center text-muted-foreground mb-6">
            Your session has been successfully scheduled. Confirmation details have been sent to your email and phone.
          </p>

          <div className="space-y-4 mb-6">
            <div className="p-4 bg-muted rounded-lg">
              <h3 className="text-sm font-semibold text-foreground mb-3">Session Details</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Booking ID</span>
                  <span className="text-sm font-medium text-foreground font-mono">{bookingDetails?.bookingId}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Helper</span>
                  <span className="text-sm font-medium text-foreground">{helper?.name}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Date</span>
                  <span className="text-sm font-medium text-foreground">{slot?.date}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Time</span>
                  <span className="text-sm font-medium text-foreground">{slot?.time}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Duration</span>
                  <span className="text-sm font-medium text-foreground">{slot?.duration} minutes</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Communication</span>
                  <div className="flex items-center gap-2">
                    <Icon name={getCommunicationIcon(slot?.communicationMethod)} size={14} className="text-primary" />
                    <span className="text-sm font-medium text-foreground">{getCommunicationLabel(slot?.communicationMethod)}</span>
                  </div>
                </div>
                {slot?.bookingChargeAmount && slot?.bookingChargeCurrency && (
                  <>
                    <div className="border-t border-border my-2"></div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Booking Charge</span>
                      <span className="text-sm font-bold text-accent">
                        {new Intl.NumberFormat('en-US', {
                          style: 'currency',
                          currency: slot?.bookingChargeCurrency
                        })?.format(slot?.bookingChargeAmount)}
                      </span>
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="p-4 bg-primary/5 rounded-lg border border-primary/20">
              <h3 className="text-sm font-semibold text-foreground mb-3">Access Instructions</h3>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-start gap-2">
                  <Icon name="Check" size={16} className="text-primary mt-0.5 flex-shrink-0" />
                  <span>You will receive a session link 15 minutes before the scheduled time</span>
                </li>
                <li className="flex items-start gap-2">
                  <Icon name="Check" size={16} className="text-primary mt-0.5 flex-shrink-0" />
                  <span>Test your audio and video settings before joining</span>
                </li>
                <li className="flex items-start gap-2">
                  <Icon name="Check" size={16} className="text-primary mt-0.5 flex-shrink-0" />
                  <span>Find a quiet, private space for your session</span>
                </li>
                <li className="flex items-start gap-2">
                  <Icon name="Check" size={16} className="text-primary mt-0.5 flex-shrink-0" />
                  <span>Have a glass of water and any notes ready</span>
                </li>
              </ul>
            </div>

            <div className="p-4 bg-muted rounded-lg">
              <h3 className="text-sm font-semibold text-foreground mb-3">Reminders</h3>
              <div className="space-y-2">
                {bookingDetails?.emailReminder && (
                  <div className="flex items-center gap-2 text-sm">
                    <Icon name="Mail" size={14} className="text-primary" />
                    <span className="text-muted-foreground">Email reminder 24 hours before session</span>
                  </div>
                )}
                {bookingDetails?.smsReminder && (
                  <div className="flex items-center gap-2 text-sm">
                    <Icon name="MessageSquare" size={14} className="text-primary" />
                    <span className="text-muted-foreground">SMS reminder 1 hour before session</span>
                  </div>
                )}
                {bookingDetails?.calendarSync && (
                  <div className="flex items-center gap-2 text-sm">
                    <Icon name="Calendar" size={14} className="text-primary" />
                    <span className="text-muted-foreground">Added to your calendar</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="p-4 bg-warning/10 rounded-lg border border-warning/20 mb-6">
            <div className="flex items-start gap-3">
              <Icon name="AlertCircle" size={20} className="text-warning mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-foreground mb-1">Cancellation Policy</p>
                <p className="text-sm text-muted-foreground">
                  Please cancel at least 24 hours in advance if you cannot attend. Late cancellations may affect your ability to book future sessions.
                </p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={onClose}
              fullWidth
            >
              Close
            </Button>
            <Button
              variant="default"
              iconName="LayoutDashboard"
              iconPosition="right"
              onClick={onViewDashboard}
              fullWidth
            >
              View Dashboard
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationModal;